import { Component, Input } from '@angular/core';
import { EnrichmentView, DiagnosisSnapshot } from '../../core/services/enrichment.service';

/**
 * Read-only "Diagnosis" section for a ticket drawer. Presentational only —
 * it renders whatever `EnrichmentView` it is given and never fetches. Designed
 * to be dropped into both the Jira and GitHub ticket drawers unchanged.
 *
 * State handling is deliberately honest: it distinguishes a concrete diagnosis
 * from "no correlated logs", "logs found but no known pattern" (inconclusive),
 * "still running", and "unavailable", and never implies certainty when the
 * backend was inconclusive.
 */
@Component({
  selector: 'app-diagnosis-section',
  templateUrl: './diagnosis-section.component.html',
})
export class DiagnosisSectionComponent {
  /** Null while the fetch is in flight, or if the fetch failed (see `loading`). */
  @Input() view: EnrichmentView | null = null;

  /** True while the enrichment fetch is in flight. */
  @Input() loading = false;

  private static readonly CATEGORY_LABELS: Record<string, string> = {
    TIMEOUT:               'Timeout',
    OUT_OF_MEMORY:         'Out of memory',
    AUTH_DENIED:           'Authorization denied',
    THROTTLING:            'Throttling',
    DEPENDENCY_UNAVAILABLE: 'Dependency unavailable',
    DNS_NETWORK:           'DNS / network',
    CONFIG_SECRET_MISSING: 'Missing config / secret',
    STARTUP_CRASH:         'Startup crash',
    STORAGE_PRESSURE:      'Storage pressure',
    QUEUE_BACKLOG:         'Queue backlog',
    UNDETERMINED:          'Undetermined',
  };

  get d(): DiagnosisSnapshot | null {
    return this.view?.diagnosis ?? null;
  }

  get state(): string {
    return this.view?.state ?? 'not_found';
  }

  /** A concrete, actionable diagnosis worth showing in full. */
  get hasConcreteDiagnosis(): boolean {
    return this.state === 'applied' && !!this.d;
  }

  categoryLabel(category: string | undefined): string {
    if (!category) return 'Unknown';
    return DiagnosisSectionComponent.CATEGORY_LABELS[category]
      ?? category.charAt(0) + category.slice(1).toLowerCase().replace(/_/g, ' ');
  }

  get confidencePct(): number {
    return Math.round((this.d?.confidenceScore ?? 0) * 100);
  }

  /** Tailwind classes for the confidence badge, keyed off the band. */
  get confidenceBadgeClass(): string {
    switch (this.d?.confidence) {
      case 'HIGH':   return 'bg-emerald-100 text-emerald-700';
      case 'MEDIUM': return 'bg-amber-100 text-amber-700';
      case 'LOW':    return 'bg-slate-200 text-slate-600';
      default:       return 'bg-slate-100 text-slate-500';
    }
  }

  /** Prose summary if the (optional, non-authoritative) LLM layer produced one. */
  get proseSummary(): string | null {
    const s = this.d?.prose?.summary;
    return typeof s === 'string' && s.trim() ? s.trim() : null;
  }

  get lastUpdated(): string | null {
    return this.d?.diagnosedAt ?? this.view?.updatedAt ?? null;
  }
}
